import Box from "@mui/material/Box";
import React, {useEffect, useMemo, useState} from "react";
import StyledLink from "../../../shared/ui/LinkStyled";
import {getRomanNumber, niceUserName, sortUserName} from "../../../utils/functions";
import CustomDataTable from "../../../shared/tables/CustomDataTable";
import GenericButton from "../../../shared/ui/GenericButton";
import {useAuth} from "../../../hooks/useAuth";
import {APIUpdateClassroom} from "../api/classroom";
import FormField from "../../../shared/form/FormField";
import {APIGetTeachers} from "../../users/api/teachers";
import useDataTable from "../../../hooks/useDataTable";
import {EmailVerifiedIcon} from "./StudentsList";
import CustomToolbar from "../../../shared/ui/CustomToolbar";

type Teacher = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    email_verified_at: string | null;
};

type Classroom = {
    id: string;
    name: string;
    grade: number;
    status: number;
    teachers: Teacher[];
    students: { id: number }[];
};

interface TeachersListProps {
    classroom: Classroom;
    updateClassroomHandler: (classroom: Classroom) => void;
}

type ConfirmCallback = () => void;

const TABLE_COLUMNS_PRINCIPAL = [
    {id: "last_name", label: "Nume și prenume", numeric: false, noWrap: true, disablePadding: false, sortable: true,},
    {id: "email", label: "E-mail", numeric: false, disablePadding: false, sortable: true,},
    {id: "email_verified_at", label: "Status", numeric: false, disablePadding: false, sortable: false,},
    {id: "actions", label: "Acțiuni", numeric: true, disablePadding: false, sticky: true,},
];

const TABLE_COLUMNS_TEACHER = [
    {id: "last_name", label: "Nume și prenume", numeric: false, disablePadding: false, noWrap: true, sortable: true,},
    {id: "email", label: "E-mail", numeric: false, disablePadding: false, sortable: true,},
    {id: "email_verified_at", label: "Status", numeric: false, noWrap: true, disablePadding: false, sortable: false,}
];

const TeachersList = ({classroom, updateClassroomHandler} : TeachersListProps) => {
    const { update, user, tenant } = useAuth();
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const isPrincipal = user.position === "principal";

    const {
        loading,
        setLoading,
        page,
        orderBy,
        setOrderBy,
        order,
        setOrder,
    } = useDataTable("", "last_name", undefined, "");

    useEffect(() => {
        setLoading(true);
        setTeachers(sortUserName(classroom.teachers, order));
        setLoading(false);
    }, [classroom.students, order]);

    const mapTeacherToRow = (teacher: Teacher) => {

        const teacherName = isPrincipal ? (
            <StyledLink to={`/profesori/${teacher.id}`}>
                {niceUserName(teacher.first_name, teacher.last_name, teacher.email)}
            </StyledLink>
        ) :  niceUserName(teacher.first_name, teacher.last_name, teacher.email);

        return {
            last_name: teacherName,
            email: teacher.email,
            email_verified_at: (
                <EmailVerifiedIcon verified={teacher.email_verified_at !== null} />
            ),
            ...(isPrincipal && {
                actions: (
                    <Box display="flex" justifyContent="end">
                        <GenericButton type="delete-from-classroom" onClick={() => confirmDelete(teacher)}/>
                    </Box>
                )
            }),
        };
    };

    const rows = useMemo(() => {
        return teachers.map(mapTeacherToRow);
    }, [teachers]);

    const handleError = (code: string) => {
        update("loading", { show: false });
        update("error", { show: true, code });
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const newOrder = isAsc ? "desc" : "asc";
        setOrder(newOrder);
        setOrderBy(property);
        setTeachers((currentTeachers) => sortUserName(currentTeachers, newOrder));
    };

    const updateClassroomTeacher = (
        updatedTeachers: Teacher[],
        callback: ConfirmCallback,) => {
        const userIds = [
            ...classroom.students.map(({ id }) => id),
            ...updatedTeachers.map(({ id }) => id),
        ];

        APIUpdateClassroom(classroom.id, {
            name: classroom.name,
            grade: classroom.grade,
            status: classroom.status,
            users: userIds })
            .then((response) => {
                if (response.data.success) {
                    // console.log("updateClassroom OK", response);
                    const updateClassroom = {
                        ...classroom,
                        ...{ teachers: updatedTeachers },
                    };
                    updateClassroomHandler(updateClassroom);
                    setTeachers(updatedTeachers);
                } else {
                    handleError("A002");
                }
            })
            .catch((err) => {
                // console.log("updateClassroom ERR", err);
                handleError("A001");
            })
            .finally(() => {
                callback();
            });
    }
    const addTeacher = (id: string, name: string, grade: number, teacher: any, callback: ConfirmCallback) => {
        if (!teacher) return;

        const alreadyExists = classroom.teachers.some(({id}) => id === teacher.id);
        const updatedteachers = alreadyExists
            ? classroom.teachers
            : [...classroom.teachers, teacher];

        updateClassroomTeacher(updatedteachers, callback);
    };

    const confirmAddTeacher = () => {
        let teacher: any;
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat user-plus",
            title: "Adaugă profesor",
            size: "large",
            message: (
                <>
                    <Box sx={{ border: "1px solid #ddd", borderRadius: "16px" }}>
                        <FormField
                            name="teachers"
                            type="autocomplete_field"
                            label="Caută profesor..."
                            optionsAPI={APIGetTeachers}
                            multiple={false}
                            filterOptions={(x: any) => x}
                            onChangeAutocomplete={(event, value) => (teacher = value)}
                        />
                    </Box>
                </>
            ),
            returnFunction: (callback: ConfirmCallback) => {
                addTeacher(classroom.id.toString(), classroom.name, classroom.grade, teacher, callback);
            },
            buttonYes: "Adaugă",
            buttonNo: "Închide",
        });
    };

    // Delete a teacher from the classroom
    const deleteTeacher = (teacher: any, callback: ConfirmCallback) => {
        if (!teacher) return;

        const updatedTeachers = classroom.teachers.filter(({id}) => id !== teacher.id);

        updateClassroomTeacher(updatedTeachers, callback);
    };

    // Confirm deleting a teacher
    const confirmDelete = (teacher: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        [key: string]: any;
    }) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat trash-can",
            title: "Ești sigur că vrei să ștergi acest profesor?",
            message: (
                <>
                    Profesorul
                    <b> {niceUserName(teacher.first_name, teacher.last_name, teacher.email)}</b> din clasa ({getRomanNumber(classroom.grade ? classroom.grade : 0)}) {classroom.name} va fi șters.<br/><br/>
                </>
            ),
            returnFunction: (callback: ConfirmCallback) => {
                deleteTeacher(teacher, callback);
            },
            buttonYes: "Șterge",
            buttonNo: "Închide",
        });
    };

    return (
        <>
            <CustomToolbar>
                <GenericButton
                    type="add-teacher-big"
                    onClick={confirmAddTeacher}
                />
            </CustomToolbar>
            <CustomDataTable
                loading={loading}
                columns={isPrincipal ? TABLE_COLUMNS_PRINCIPAL : TABLE_COLUMNS_TEACHER}
                rows={rows}
                rowsPerPage={teachers.length}
                page={page}
                totalRows={teachers.length}
                handleRequestSort={handleRequestSort}
                orderBy={orderBy}
                order={order}
                hasSearch={false}
            />
        </>
    )
}

export default TeachersList;